import {
  Component,
  OnInit,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexGrid,
  ApexTooltip,
  ApexStroke,
  ApexFill,
} from 'ng-apexcharts';
import { Router } from "@angular/router";

// Services
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";

import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { SYSTEM_ADMIN_HOME_I18N } from "./system-admin-home.i18n";

@Component({
  selector: "app-system-admin-home",
  templateUrl: "./system-admin-home.component.html",
  styleUrls: ["./system-admin-home.component.scss"],
  imports: SHARED_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemAdminHomeComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutBreakpoint = inject(LayoutBreakpointService);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = SYSTEM_ADMIN_HOME_I18N;

  isLoading = signal(true);
  isHandset$: Observable<boolean>;

  // section
  selectedSectionGroup: any;

  error: any;

  // Number of Accounts
  createdAccountNum = 0;
  removedAccountNum = 0;
  totalAccountNum = 0;

  // Number of Accounts: Month to Month Basis
  createdAccountNumMTMB = 0;
  createdAccountNumMTMBsign = true;

  removedAccountNumMTMB = 0;
  removedAccountNumMTMBsign = true;

  totalAccountNumMTMB = 0;
  totalAccountNumMTMBsign = true;

  // ApexCharts configuration
  chartSeries: ApexAxisChartSeries = [{ name: SYSTEM_ADMIN_HOME_I18N.chart.yAxisAccountCount, data: [] }];
  chartOptions: ApexChart = {
    type: 'bar',
    height: 300,
    foreColor: '#757575',
    toolbar: { show: false },
    animations: { enabled: true, speed: 500 },
  };
  chartXAxis: ApexXAxis = {
    categories: [],
    title: { text: SYSTEM_ADMIN_HOME_I18N.chart.xAxisPeriodOfUse, style: { color: '#757575', fontSize: '12px' } },
    labels: { style: { colors: '#757575', fontSize: '11px' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  };
  chartYAxis: ApexYAxis = {
    min: 0,
    title: { text: SYSTEM_ADMIN_HOME_I18N.chart.yAxisAccountCount, style: { color: '#757575', fontSize: '12px' } },
    labels: {
      style: { colors: '#757575', fontSize: '11px' },
      formatter: (val: number) => Math.round(val).toString(),
    },
  };
  chartDataLabels: ApexDataLabels = { enabled: false };
  chartPlotOptions: ApexPlotOptions = {
    bar: { borderRadius: 5, columnWidth: '55%' },
  };
  chartStroke: ApexStroke = {
    show: true,
    width: 2,
    colors: ['transparent'],
  };
  chartFill: ApexFill = { opacity: 0.88 };
  chartGrid: ApexGrid = {
    borderColor: '#E0E0E0',
    strokeDashArray: 1,
    xaxis: { lines: { show: false } },
  };
  chartTooltip: ApexTooltip = {
    theme: 'dark',
    marker: { show: true },
    y: { formatter: (val: number) => `${Math.round(val)} 件` },
  };
  chartColors = ['#6750A4'];

  // Notices
  notices: { date: string; text: string; link?: string[] }[] = [
    { date: '2025/03/24', text: '「月次サマリー」から月間のデータ使用量をご確認いただけるようになりました。' },
    { date: '2025/02/07', text: '連絡帳をご利用の方向けに通知機能を追加しました。' },
    { date: '2025/02/07', text: 'ユーザーサマリーをご利用の方向けのPDF出力にGOKURIレコードを追加できるように修正しました。' },
    { date: '2024/09/25', text: 'ユーザーサマリーをご利用の方向けのPDF出力のデザインを修正しました。' },
    { date: '2024/09/09', text: 'GOST機能をアップデートしました。各レコード表示UIの修正をしました。' },
    { date: '2024/08/02', text: 'ユーザーリストのUIの修正を行い、最終更新日でソートできるように変更しました。' },
    { date: '2023/09/26', text: 'ユーザーサマリーをご利用の方向けに、日付選択やデータ修正機能を実装しました。' },
    { date: '2023/09/01', text: 'GOKURIクラウドサービスのバージョンアップを行い、UIを変更ました。' },
    { date: '2023/02/20', text: 'GOKURIクラウドサービスのバージョンアップを行いました。' },
    { date: '2022/10/08', text: 'GOKURIアプリのバージョンアップを行いました。' },
  ];

  constructor(
    private router: Router,
    public common: DatabaseCacheService,
  ) {}

  ngOnInit() {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;

    // section
    this.selectedSectionGroup = this.common.getSelectedSectionGroup();
    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectedSectionGroup = this.common.getSelectedSectionGroup();
      });

    // Data comes from cache, loading is fast
    setTimeout(() => this.isLoading.set(false), 300);
  }
}
